// pages/admin/FeeCollection.jsx
import React, { useState, useEffect } from 'react';
import { feeAPI, adminAPI } from '../../services/api';
import toast from 'react-hot-toast';

const FeeCollection = () => {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [receiptTemplates, setReceiptTemplates] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [paymentData, setPaymentData] = useState({ student_fee_id: null, amount: '', method: 'cash', reference: '' });
  const [adjustData, setAdjustData] = useState({ fee_structure_id: null, amount: '', reason: '' });
  const [history, setHistory] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedClass || selectedSession || selectedTerm || selectedStatus || searchQuery) {
      fetchStudentsBalance();
    }
  }, [selectedClass, selectedSession, selectedTerm, selectedStatus, searchQuery]);

  const fetchInitialData = async () => {
    try {
      const [classesRes, sessionsRes, templatesRes] = await Promise.all([
        adminAPI.getClasses(),
        adminAPI.getAcademicSessions(),
        feeAPI.getReceiptTemplates(),
      ]);
      setClasses(classesRes.data);
      setSessions(sessionsRes.data);
      setReceiptTemplates(templatesRes.data);
      if (templatesRes.data.length > 0) {
        const defaultTemplate = templatesRes.data.find(t => t.is_default) || templatesRes.data[0];
        setSelectedTemplateId(defaultTemplate.id);
      }
    } catch (err) {
      toast.error('Failed to load initial data');
    }
  };

  const fetchStudentsBalance = async () => {
    setLoading(true);
    try {
      const params = {
        class_id: selectedClass || undefined,
        session_id: selectedSession || undefined,
        term_id: selectedTerm || undefined,
        status: selectedStatus || undefined,
        search: searchQuery || undefined,
      };
      const res = await feeAPI.getStudentsBalance(params);
      setStudents(res.data);
    } catch (err) {
      toast.error('Failed to load student balances');
    } finally {
      setLoading(false);
    }
  };

  const handleViewStudent = async (studentId) => {
    try {
      const detail = await feeAPI.getStudentFeeDetail(studentId);
      const historyRes = await feeAPI.getPaymentHistory(studentId);
      setSelectedStudent(detail.data);
      setHistory(historyRes.data);
    } catch (err) {
      toast.error('Failed to load student details');
    }
  };

  const handleCapturePayment = async () => {
    try {
      await feeAPI.capturePayment({
        student_fee_id: paymentData.student_fee_id,
        amount: parseFloat(paymentData.amount),
        method: paymentData.method,
        reference: paymentData.reference || undefined,
      });
      toast.success('Payment captured');
      setShowPaymentModal(false);
      fetchStudentsBalance();
      if (selectedStudent) handleViewStudent(selectedStudent.student_id);
    } catch (err) {
      toast.error('Payment failed');
    }
  };

  const handleAdjustBalance = async () => {
    try {
      await feeAPI.adjustBalance({
        student_id: selectedStudent.student_id,
        fee_structure_id: adjustData.fee_structure_id || null,
        amount: parseFloat(adjustData.amount),
        reason: adjustData.reason,
      });
      toast.success('Balance adjusted');
      setShowAdjustModal(false);
      fetchStudentsBalance();
      if (selectedStudent) handleViewStudent(selectedStudent.student_id);
    } catch (err) {
      toast.error('Adjustment failed');
    }
  };

  // ✅ Fixed: do not send empty template_id
  const handlePrintReceipt = async (paymentId) => {
    try {
      const templateId = selectedTemplateId || undefined;
      const response = await feeAPI.generateReceipt(paymentId, templateId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt-${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to generate receipt');
    }
  };

  // ✅ NEW: Print all payments summary for the student
  const handlePrintStudentReceipt = async () => {
    if (!selectedStudent) return;
    try {
      const templateId = selectedTemplateId || undefined;
      const response = await feeAPI.generateStudentReceipt(selectedStudent.student_id, templateId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `student_receipt_${selectedStudent.student_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to generate student receipt');
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = {
        class_id: selectedClass || undefined,
        session_id: selectedSession || undefined,
        term_id: selectedTerm || undefined,
        status: selectedStatus || undefined,
      };
      const response = await feeAPI.exportBalance(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'balance_report.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Export failed');
    }
  };

  return (
    <div className="space-y-6 p-4">
      <h1 className="text-2xl font-bold text-gray-800">Fee Collection</h1>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end bg-white p-4 rounded shadow">
        <div>
          <label className="block text-sm font-medium text-gray-700">Class</label>
          <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} className="input-field">
            <option value="">All Classes</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.class_name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Session</label>
          <select value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)} className="input-field">
            <option value="">All Sessions</option>
            {sessions.map(s => <option key={s.id} value={s.id}>{s.academic_session}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Term</label>
          <select value={selectedTerm} onChange={(e) => setSelectedTerm(e.target.value)} className="input-field">
            <option value="">All Terms</option>
            {terms.map(t => <option key={t.id} value={t.id}>{t.term}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} className="input-field">
            <option value="">All</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
            <option value="overpaid">Overpaid</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700">Search</label>
          <input
            type="text"
            placeholder="Reg or Name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={fetchStudentsBalance} className="btn-secondary">Refresh</button>
          <button onClick={handleExportCSV} className="btn-primary">Export CSV</button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-center py-10">Loading...</p>
      ) : (
        <div className="overflow-x-auto shadow rounded-lg bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reg</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Due</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {students.map(s => (
                <tr key={s.student_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{s.student_reg}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{s.full_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{s.class_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{s.total_due}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{s.total_paid}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${s.balance > 0 ? 'text-red-600' : s.balance < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                    {s.balance}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      s.status === 'paid' ? 'bg-green-100 text-green-800' :
                      s.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                      s.status === 'overpaid' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button onClick={() => handleViewStudent(s.student_id)} className="btn-sm text-indigo-600 hover:text-indigo-900">
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {students.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">No students found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Student Detail Modal */}
      {selectedStudent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold">{selectedStudent.full_name}</h2>
                <p className="text-sm text-gray-600">{selectedStudent.class_name} | {selectedStudent.student_reg}</p>
              </div>
              <button onClick={() => setSelectedStudent(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div><span className="font-medium">Total Due:</span> {selectedStudent.total_due}</div>
              <div><span className="font-medium">Paid:</span> {selectedStudent.total_paid}</div>
              <div><span className="font-medium">Balance:</span> {selectedStudent.balance}</div>
            </div>

            <h3 className="mt-4 font-semibold">Fee Items</h3>
            <ul className="divide-y divide-gray-200">
              {selectedStudent.fee_items.map(item => (
                <li key={item.id} className="py-2 flex justify-between items-center">
                  <span>{item.category}: Due {item.amount_due}, Paid {item.amount_paid}, Balance {item.balance}</span>
                  <div className="space-x-2">
                    <button onClick={() => {
                      setPaymentData({ ...paymentData, student_fee_id: item.id });
                      setShowPaymentModal(true);
                    }} className="text-sm text-green-600 hover:text-green-800">Pay</button>
                    <button onClick={() => {
                      setAdjustData({ ...adjustData, fee_structure_id: item.id });
                      setShowAdjustModal(true);
                    }} className="text-sm text-blue-600 hover:text-blue-800">Adjust</button>
                  </div>
                </li>
              ))}
            </ul>

            <h3 className="mt-4 font-semibold">Payment History</h3>
            <div className="flex items-center gap-4 mt-2 flex-wrap">
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="input-field text-sm"
              >
                {receiptTemplates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <button onClick={handlePrintStudentReceipt} className="btn-sm text-indigo-600 hover:text-indigo-800">
                Print All Payments
              </button>
            </div>
            <ul className="divide-y divide-gray-200 mt-2">
              {history.map(p => (
                <li key={p.id} className="py-2 flex justify-between items-center">
                  <span>{p.created_at}: {p.amount} via {p.method} (Ref: {p.reference})</span>
                  <button onClick={() => handlePrintReceipt(p.id)} className="text-sm text-indigo-600 hover:text-indigo-800">
                    Receipt
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold mb-4">Capture Payment</h3>
            <div className="space-y-3">
              <input
                type="number"
                placeholder="Amount"
                value={paymentData.amount}
                onChange={e => setPaymentData({...paymentData, amount: e.target.value})}
                className="input-field w-full"
              />
              <select
                value={paymentData.method}
                onChange={e => setPaymentData({...paymentData, method: e.target.value})}
                className="input-field w-full"
              >
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="pos">POS</option>
              </select>
              <input
                type="text"
                placeholder="Reference (optional)"
                value={paymentData.reference}
                onChange={e => setPaymentData({...paymentData, reference: e.target.value})}
                className="input-field w-full"
              />
              <div className="flex gap-2">
                <button onClick={handleCapturePayment} className="btn-primary">Submit</button>
                <button onClick={() => setShowPaymentModal(false)} className="btn-secondary">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Adjust Modal */}
      {showAdjustModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold mb-4">Adjust Balance</h3>
            <div className="space-y-3">
              <input
                type="number"
                placeholder="Amount (+ credit, - debit)"
                value={adjustData.amount}
                onChange={e => setAdjustData({...adjustData, amount: e.target.value})}
                className="input-field w-full"
              />
              <input
                type="text"
                placeholder="Reason"
                value={adjustData.reason}
                onChange={e => setAdjustData({...adjustData, reason: e.target.value})}
                className="input-field w-full"
              />
              <div className="flex gap-2">
                <button onClick={handleAdjustBalance} className="btn-primary">Apply</button>
                <button onClick={() => setShowAdjustModal(false)} className="btn-secondary">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeCollection;